import type { DependencyList } from 'react';
import { useEffect, useRef } from 'react';

interface EffectConfig {
  delay: number;
  runOnMount?: boolean;
}

export const useDebouncedEffect = (
  effect: () => void,
  deps: DependencyList,
  delayOrConfig: number | EffectConfig
) => {
  const searchTimeoutId = useRef<number | undefined>(undefined);
  const didMount = useRef(false);

  const isNumber = typeof delayOrConfig === 'number';
  const delay = isNumber ? delayOrConfig : delayOrConfig.delay;
  const runOnMount = isNumber ? true : !!delayOrConfig.runOnMount;

  useEffect(
    () => {
      if (!runOnMount && !didMount.current) {
        didMount.current = true;
      } else {
        // Use timeouts to debounce
        clearTimeout(searchTimeoutId.current!);
        searchTimeoutId.current = window.setTimeout(effect, delay);
      }

      return () => {
        clearTimeout(searchTimeoutId.current!);
      };
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
    [...deps, delay, runOnMount]
  );
};

export const useThrottledEffect = (effect: () => void, deps: DependencyList, delayMs: number) => {
  const freeToFireRef = useRef<boolean>(true);
  const waitingEffect = useRef<null | (() => void)>();

  const wrapper = (cb: () => void) => {
    if (!freeToFireRef.current) {
      waitingEffect.current = effect;
      return;
    }
    freeToFireRef.current = false;
    window.setTimeout(() => {
      freeToFireRef.current = true;
      if (waitingEffect.current != null) {
        const next = waitingEffect.current;
        waitingEffect.current = null;
        wrapper(next);
      }
    }, delayMs);
    cb();
  };

  useEffect(
    () => {
      wrapper(effect);
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
    [...(deps ?? []), delayMs]
  );
};
